// StorageChip — colored pill keyed by canonical storage_condition.id.
// Ported from tag-selectors.jsx (lines 24-45).

import { STORAGE_TONES } from "./constants";
import type { StorageConditionRow } from "./_types";

type Props = {
  /** storage_condition.id (matches platform.inventory_type_storage_condition.id). */
  id: string;
  /** Catalog row used to look up code/hint. If absent, falls back to id as
   *  the displayed code. */
  row?: StorageConditionRow | null;
  size?: "sm" | "lg";
  showHint?: boolean;
};

export function StorageChip({ id, row, size = "sm", showHint }: Props) {
  const tone = STORAGE_TONES[id] ?? STORAGE_TONES.ambient!;
  const pad = size === "lg" ? "4px 10px" : "2px 7px";
  const font = size === "lg" ? 11 : 10;
  // Never display a raw id/UUID. Show the catalog row's code; if no row, only
  // show `id` when it's a known canonical slug (e.g. "ambient"), else nothing.
  const code = row?.code ?? (id in STORAGE_TONES ? id : "");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: pad,
        borderRadius: 999,
        background: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.line}`,
        fontFamily: "var(--font-mono)",
        fontSize: font,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
        }}
      />
      {code}
      {showHint && row?.hint && (
        <span
          style={{ fontSize: font - 1, opacity: 0.7, textTransform: "none" }}
        >
          {row.hint}
        </span>
      )}
    </span>
  );
}

/** Storage chip for a raw (uncanonical) storage condition value. */
export function RawStorageChip({ text }: { text: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 7px",
        borderRadius: 999,
        background: "var(--bg-sunk)",
        color: "var(--ink-2)",
        border: "1px solid var(--line)",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.04em",
      }}
    >
      <span style={{ fontStyle: "italic" }}>{text}</span>
      <span
        style={{
          fontSize: 8.5,
          fontWeight: 700,
          opacity: 0.65,
          padding: "0 4px",
          borderRadius: 2,
          background: "var(--line)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        custom
      </span>
    </span>
  );
}
